// stores/audioStore.ts
import { defineStore } from 'pinia'
import { ref, computed, watch, shallowRef } from 'vue'
import { useChartRenderStore } from './ChartRenderStore'
import { useLogStore } from './LogStore'
import { useIDBStore } from './IDBStore'

export const useAudioStore = defineStore('audio', () => {

  // --------------------------------------------------------
  // CONSTANTS 
  // --------------------------------------------------------

  const logStore = useLogStore()
  const idbStore = useIDBStore()

  const audioElement = document.createElement('audio')
  const LOG_GROUP = 'Audio'

  const audioContext = new window.AudioContext()
  const mediaElementSource = audioContext.createMediaElementSource(audioElement)

  // --------------------------------------------------------
  // STATE 
  // --------------------------------------------------------

  let animationFrameId = 0

  const filter = audioContext.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 1000;
  filter.Q.value = 1;

  const dryGain = audioContext.createGain();
  const wetGain = audioContext.createGain();

  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 2048;
  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  const isFilterActive = ref(false)

  let audioFileObjectUrl = null

  const isPlaying = ref(false)
  const currentTime = ref(0)
  const playbackRate = ref(1)
  const duration = ref(0)
  const hasAudioLoaded = ref(false)

  const waveformData = shallowRef(null)

  // --------------------------------------------------------
  // GETTERS 
  // --------------------------------------------------------

  function getWaverformData(audioFile) {
    logStore.sendLog(LOG_GROUP, `Starting audio waveform generation`)
    return new Promise((resolve, reject) => {
      const audioCtx = new window.AudioContext();

      audioFile.arrayBuffer()
        .then(arrayBuffer => audioCtx.decodeAudioData(arrayBuffer))
        .then(audioBuffer => {
          try {
            waveformData.value = new Map()

            //be sure to insert from biggest to smallest
            waveformData.value.set(1, processAudioBuffer(audioBuffer, 1));
            waveformData.value.set(0.5, processAudioBuffer(audioBuffer, 0.5));
            waveformData.value.set(0.1, processAudioBuffer(audioBuffer, 0.1));

            logStore.sendLog(LOG_GROUP, `Finished audio waveform generation`)
            resolve(waveformData.value);
          } catch (e) {
            reject(`Error processing audio buffer: ${e}`);
          }
        })
        .catch(e => reject(`Error during decoding: ${e}`));
    });
  }


  // --------------------------------------------------------
  // ACTIONS 
  // --------------------------------------------------------


  const setFilterFrequency = (value) => {
    if (value <= 0 || isNaN(value)) return setFilterFrequency(1)
    filter.frequency.value = value;
    return value
  }

  const setFilterQ = (value) => {
    if (value <= 0 || isNaN(value)) return setFilterQ(0.01)
    filter.Q.value = value;
    return value
  }


  const loadAudio = async (audioFile) => {
    if (hasAudioLoaded.value) {
      URL.revokeObjectURL(audioFileObjectUrl)
      logStore.sendLog(LOG_GROUP, "Unloaded current audio file.")
    }
    audioFileObjectUrl = URL.createObjectURL(audioFile);
    audioElement.src = audioFileObjectUrl
    audioElement.load();
    playbackRate.value = 1

    idbStore.setCurrentAudioTitle(audioFile.name)

    getWaverformData(audioFile)
  }

  const unloadAudio = () => {
    audioElement.pause();
    audioElement.src = ''
    audioElement.load();
    idbStore.setCurrentAudioTitle(null)
    duration.value = 0
    waveformData.value = new Map()
  }

  const togglePausePlay = () => {
    if (hasAudioLoaded.value) {
      if (isPlaying.value) pause(); else play();
    }
  }

  const play = () => {
    audioElement.play().catch(e => {
      logStore.sendLog(LOG_GROUP, `Failed to start audio playing: ${e}`, e)
    })
  }

  const pause = () => { audioElement.pause() }

  const seek = (time) => {
    if (hasAudioLoaded.value) {
      time = Math.max(Math.min(audioElement.duration, time), 0)
      audioElement.currentTime = time
    }
  }

  const setPlaybackRate = (rate) => {
    rate = parseFloat(rate)
    try {
      audioElement.playbackRate = rate
    } catch (e) {
      logStore.sendLog(LOG_GROUP, `Failed to audio playback rate set to: ${rate}: ${e}`, e)
      return
    }
    playbackRate.value = rate
    logStore.sendLog(LOG_GROUP, `Audio playback rate set to: ${rate}.`)
  }

  const setVolume = (volume) => {
    volume = Math.min(Math.max(volume, 0), 1)
    audioElement.volume = volume
  }

  const animateFrame = () => {
    if (!isPlaying.value)
      return

    currentTime.value = audioElement.currentTime
    animationFrameId = requestAnimationFrame(animateFrame)
  }

  function processAudioBuffer(audioBuffer, sampleCountPerMillisecondDivisor) {
    const sampleCountPerMillisecond = audioBuffer.sampleRate / 1000 / sampleCountPerMillisecondDivisor;
    const channelData = audioBuffer.getChannelData(0);
    const data = []

    if (sampleCountPerMillisecond <= 0) return []

    let i = 0;
    while (i < audioBuffer.length) {
      const start = Math.trunc(i);
      const end = Math.min(i + sampleCountPerMillisecond, audioBuffer.length);
      const count = end - start;

      let min = Infinity;
      let max = -Infinity;

      for (let j = 0; j < count; j++) {
        const value = channelData[start + j];
        if (value > max) max = value;
        if (value < min) min = value;
      }

      data.push({ min, max });
      i += sampleCountPerMillisecond;
    }

    return data;
  }

  // --------------------------------------------------------
  // MISC 
  // --------------------------------------------------------

  audioElement.addEventListener('canplaythrough', (_e) => {
    if (!hasAudioLoaded.value) //to avoid excessive logs when seeking
      logStore.sendLog(LOG_GROUP, "Audio finished loading.")
    hasAudioLoaded.value = true
    duration.value = audioElement.duration
  })
  audioElement.addEventListener('emptied', (_e) => {
    hasAudioLoaded.value = false
  })
  audioElement.addEventListener('play', (_e) => {
    if (!isPlaying.value)
      isPlaying.value = true
    animateFrame()
  })
  audioElement.addEventListener('pause', (_e) => {
    cancelAnimationFrame(animationFrameId);
    isPlaying.value = false
  })
  audioElement.addEventListener('loadstart', (_e) => {
    logStore.sendLog(LOG_GROUP, "Audio started loading.")
  })
  audioElement.addEventListener('seeked', (_e) => {
    currentTime.value = audioElement.currentTime
  })

  document.body.appendChild(audioElement)

  mediaElementSource.connect(dryGain);
  mediaElementSource.connect(filter);
  filter.connect(wetGain);

  dryGain.connect(analyser);
  wetGain.connect(analyser);

  analyser.connect(audioContext.destination);

  watch(isFilterActive, async (newValue, oldValue) => {
    if (newValue) {
      wetGain.gain.value = 1
      dryGain.gain.value = 0
    } else {
      wetGain.gain.value = 0
      dryGain.gain.value = 1
    }
  }, { immediate: true })

  return {
    unloadAudio,
    waveformData,
    bufferLength,
    dataArray,
    filter,
    analyser,
    isFilterActive,
    audioElement,
    audioContext,
    isPlaying,
    currentTime,
    duration,
    playbackRate,
    hasAudioLoaded,
    loadAudio,
    togglePausePlay,
    play,
    pause,
    seek,
    setPlaybackRate,
    setVolume,
    setFilterFrequency,
    setFilterQ,
  }
})
